<template>
    <div id="AutomationSandbox">
        <div class="header">
            <div class="header-icon">
                <i class="material-icons">delete_outline</i>
            </div>
            <div class="header-text">
                <h1>{{ $t("automation.sandboxPage.title") }}</h1>
                <p>{{ $t("automation.sandboxPage.description") }}</p>
            </div>
        </div>

        <div v-if="loading" class="automations-loading">
            <div v-for="skltn in 3" class="automation automation-skeleton" :key="skltn">
                <a-skeleton :active="true" :paragraph="{ rows: 3 }" />
            </div>
        </div>

        <div v-else-if="deletedAutomations.length === 0" class="empty-state">
            <i class="material-icons">delete_outline</i>
            <p>{{ $t("automation.sandboxPage.emptyState") }}</p>
        </div>

        <div v-else class="automations">
            <div
                class="automation automation-deleted"
                v-for="automation in deletedAutomations"
                :key="automation._id || automation.id"
            >
                <div class="automation-header">
                    <div class="automation-title">{{ automation.name || $t("automation.sandboxPage.unnamedAutomation") }}</div>
                    <a-popover trigger="click" class="automation-menu-content">
                        <div slot="content">
                            <div
                                class="automation-menu-popover-items"
                                @click="restoreAutomation(automation)"
                            >
                                <span><a-icon type="undo" class="px-1" /></span>
                                <span>{{ $t("automation.sandboxPage.restore") }}</span>
                            </div>
                        </div>
                        <a-button
                            class="automation-menu-btn"
                            @click="(e) => e.stopPropagation()"
                        >
                            <span class="material-icons">more_vert</span>
                        </a-button>
                    </a-popover>
                </div>
                <div class="automation-content">
                    <div class="automation-trigger">
                        <template v-if="automation.options && automation.options.trigger === 'timer'">
                            <i class="material-icons">timer</i>
                            <span>{{ $t("automation.sandboxPage.timerAutomation") }}</span>
                        </template>
                        <template v-else-if="automation.options && automation.options.trigger === 'event'">
                            <i class="material-icons">event</i>
                            <span>{{ $t("automation.sandboxPage.eventAutomation") }}{{ automation.options.event && automation.options.event.type ? `: ${automation.options.event.type}` : '' }}</span>
                        </template>
                        <template v-else-if="automation.options && automation.options.trigger === 'webhook'">
                            <i class="material-icons">webhook</i>
                            <span>{{ $t("automation.sandboxPage.webhook") }}</span>
                        </template>
                        <template v-else-if="automation.options && automation.options.trigger === 'button'">
                            <i class="material-icons">smart_button</i>
                            <span>{{ $t("automation.sandboxPage.buttonAutomation") }}</span>
                        </template>
                        <template v-else-if="automation.options && automation.options.trigger === 'table-button'">
                            <i class="material-icons">smart_button</i>
                            <span>{{ $t("automation.sandboxPage.tableButtonAutomation") }}</span>
                        </template>
                        <template v-else-if="automation.options && automation.options.trigger === 'chatbot'">
                            <i class="material-icons">smart_toy</i>
                            <span>{{ $t("automation.sandboxPage.chatbotAutomation") }}</span>
                        </template>
                        <template v-else>
                            <i class="material-icons">settings</i>
                            <span>{{ automation.options && automation.options.trigger ? automation.options.trigger : $t("automation.sandboxPage.unknown") }}</span>
                        </template>
                    </div>
                    <div class="automation-description" v-if="automation.description">
                        {{ automation.description }}
                    </div>
                    <div class="automation-meta">
                        <div class="meta-item">
                            <i class="material-icons">schedule</i>
                            <span>{{ formatDeletedDate(automation.deletedAt) }}</span>
                        </div>
                        <div class="meta-item" v-if="automation.deletedBy">
                            <i class="material-icons">person</i>
                            <span>{{ getDeletedByText(automation.deletedBy) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { AUTOMATION_API } from "../../../../../api/automation";
import moment from "moment";

export default {
    name: "AutomationSanbox",
    data() {
        return {
            loading: true,
            deletedAutomations: [],
        };
    },
    methods: {
        formatDeletedDate(date) {
            if (!date) return this.$t("automation.sandboxPage.unknown");
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        getDeletedByText(deletedBy) {
            if (!deletedBy) return this.$t("automation.sandboxPage.unknown");
            // deletedBy populate edilmişse name veya email göster
            if (typeof deletedBy === "object" && deletedBy.name) {
                return deletedBy.name;
            }
            if (typeof deletedBy === "object" && deletedBy.email) {
                return deletedBy.email;
            }
            // Eğer sadece ID ise
            return this.$t("automation.sandboxPage.user");
        },
        async restoreAutomation(automation) {
            const vm = this;
            this.$confirm({
                title: this.$t("automation.sandboxPage.restoreConfirmTitle"),
                content: this.$t("automation.sandboxPage.restoreConfirmContent", {
                    name: automation.name || this.$t("automation.sandboxPage.unnamedAutomation"),
                }),
                onOk: async () => {
                    try {
                        await AUTOMATION_API.restoreAutomation(automation._id || automation.id);
                        vm.$message.success(vm.$t("automation.sandboxPage.restoreSuccess"));
                        await vm.loadDeletedAutomations();
                    } catch (error) {
                        console.error("Restore error:", error);
                        vm.$message.error(error.response?.data?.error || vm.$t("automation.sandboxPage.restoreFailed"));
                    }
                },
            });
        },
        async loadDeletedAutomations() {
            try {
                this.loading = true;
                const data = await AUTOMATION_API.listDeletedAutomations();
                this.deletedAutomations = data.map((x) => {
                    x.id = x._id;
                    return x;
                });
            } catch (error) {
                console.error("Load deleted automations error:", error);
                this.$message.error(this.$t("automation.sandboxPage.loadError"));
            } finally {
                this.loading = false;
            }
        },
    },
    async mounted() {
        await this.loadDeletedAutomations();
    },
};
</script>
<style lang="scss" scoped>
#AutomationSandbox {
    padding: 1.5rem;

    .header {
        display: flex;
        align-items: flex-start;
        padding: 1.5rem;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        margin-bottom: 1.5rem;

        .header-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: #f3f4f6;
            border-radius: 6px;
            margin-right: 1rem;
            flex-shrink: 0;

            .material-icons {
                font-size: 20px;
                color: #6b7280;
            }
        }

        .header-text {
            flex: 1;
            min-width: 0;

            h1 {
                font-size: 1.25rem;
                font-weight: 600;
                color: #111827;
                margin: 0 0 0.5rem 0;
            }

            p {
                font-size: 0.875rem;
                color: #6b7280;
                margin: 0;
                line-height: 1.5;
            }
        }
    }

    .automations-loading {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;

        .automation-skeleton {
            width: 280px;
            height: 200px;
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 1rem;
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 4rem 2rem;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        text-align: center;

        .material-icons {
            font-size: 64px;
            color: #d1d5db;
            margin-bottom: 1rem;
        }

        p {
            font-size: 1rem;
            color: #6b7280;
            margin: 0;
        }
    }

    .automations {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;

        .automation {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            width: 280px;
            min-width: 250px;
            max-width: 280px;
            display: flex;
            flex-direction: column;
            transition: all 0.2s;
            position: relative;

            &:hover {
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                border-color: #d1d5db;
            }

            &.automation-deleted {
                opacity: 0.85;
                border-left: 3px solid #ef4444;
            }

            .automation-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                padding: 1rem 1rem 0.5rem 1rem;
                border-bottom: 1px solid #f3f4f6;

                .automation-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #111827;
                    flex: 1;
                    margin-right: 0.5rem;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .automation-menu-btn {
                    border: none;
                    background: transparent;
                    padding: 0.25rem;
                    min-width: auto;
                    height: auto;
                    box-shadow: none;

                    .material-icons {
                        font-size: 20px;
                        color: #6b7280;
                    }

                    &:hover {
                        background: #f3f4f6;
                    }
                }
            }

            .automation-content {
                padding: 1rem;
                flex: 1;
                display: flex;
                flex-direction: column;

                .automation-trigger {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                    color: #374151;
                    font-weight: 500;
                    margin-bottom: 0.75rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 1px solid #f3f4f6;

                    .material-icons {
                        font-size: 18px;
                        color: #6b7280;
                    }

                    span {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }

                .automation-description {
                    font-size: 0.875rem;
                    color: #6b7280;
                    margin-bottom: 1rem;
                    line-height: 1.5;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .automation-meta {
                    margin-top: auto;
                    padding-top: 0.75rem;
                    border-top: 1px solid #f3f4f6;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;

                    .meta-item {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        font-size: 0.75rem;
                        color: #9ca3af;

                        .material-icons {
                            font-size: 16px;
                        }
                    }
                }
            }
        }
    }
}

.automation-menu-popover-items {
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        color: #1890ff;
        background: #f0f7ff;
    }
}
</style>
